import { Component, Input, Output, EventEmitter, OnInit, OnDestroy, AfterViewInit, ViewChildren, QueryList } from '@angular/core';
import { GoogleMap } from '@angular/google-maps';
import { Router, ActivatedRoute } from '@angular/router';
import { forkJoin, of, Subscription } from 'rxjs';
import { catchError } from 'rxjs/operators';
import dayjs from 'dayjs';
import { AnalysisManagementService } from '../services/analysis-management.service';
import { FieldImageryLegendDto } from '../dto/FieldImageryLegendDto';
import { FieldLegend } from '../models/fieldLegend';
import { MapsTypesAvailabilityDto } from '../dto/MapsTypesAvailabilityDto';
import { environment } from '../../environments/environment';
import { NavBarComponent } from '../nav-bar/nav-bar.component';
import { ChemicalCompareEntry } from './chemical-compare-models/chemical-compare-entry-model';
import { ChemicalCompareSideModel } from './chemical-compare-models/chemical-compare-side-model';
import { ChemicalCompareMapSyncService } from './chemical-compare-services/chemical-compare-map-sync.service';
import { ChemicalCompareService } from './chemical-compare-services/chemical-compare.service';

@Component({
    selector: 'app-chemical-compare',
    templateUrl: './chemical-compare.component.html',
    styleUrls: ['./chemical-compare.component.css'],
    standalone: false
})
export class ChemicalCompareComponent implements OnInit, AfterViewInit, OnDestroy {

    @Input() fieldId: number;
    @Input() fieldName: string = '';
    @Input() fieldSize: number;
    @Input() fieldPolygon: any[] = [];
    @Output() closed = new EventEmitter<void>();
    @ViewChildren(GoogleMap) maps: QueryList<GoogleMap>;

    chemCompareLeft: ChemicalCompareSideModel = { soilType: null, availableYears: [], allMaps: [], selectedYear: null, entries: [] };
    chemCompareRight: ChemicalCompareSideModel = { soilType: null, availableYears: [], allMaps: [], selectedYear: null, entries: [] };
    chemCompareChemicalTypes: { type: string, alias: string }[] = [];
    alignedEntries: { left: ChemicalCompareEntry | null, right: ChemicalCompareEntry | null }[] = [];

    showLegends: boolean = false;
    showLegendsToggle: boolean = false;

    legendsMap: Record<string, FieldLegend[]> = {};
    private legendsLoaded = new Set<string>();
    boundsCache: Record<string, google.maps.LatLngBounds> = {};
    centerCache: Record<string, google.maps.LatLngLiteral> = {};

    private mapSyncSub: Subscription;
    private mapsChangeSub: Subscription;
    private zoomSub: Subscription;
    private resetSub: Subscription;
    private legendSub: Subscription;
    private isUpdatingFromSync = false;
    private wheelListeners: { div: HTMLElement, handler: (e: WheelEvent) => void }[] = [];
    private onKeyDown: (e: KeyboardEvent) => void;
    private onKeyUp: (e: KeyboardEvent) => void;
    private onBlur: () => void;

    mapOptions: google.maps.MapOptions = {
        mapTypeId: 'hybrid',
        disableDefaultUI: true,
        zoomControl: false,
        scrollwheel: false,
        disableDoubleClickZoom: true,
        gestureHandling: 'greedy',
        isFractionalZoomEnabled: true
    };

    polygonOptions = {
        strokeColor: environment.google.mapConfig.polygonStrokeColour,
        strokeWeight: 4,
        fillOpacity: 0.0
    };

    constructor(
        private analysisManagementService: AnalysisManagementService,
        private chemCompareMapSyncService: ChemicalCompareMapSyncService,
        private chemicalCompareService: ChemicalCompareService,
        private route: ActivatedRoute,
        public router: Router,
        public nav: NavBarComponent
    ) {}

    ngOnInit(): void {
        this.chemCompareMapSyncService.resetLegends();

        const fieldIdParam = this.route.snapshot.paramMap.get('fieldId');
        if (fieldIdParam) {
            this.fieldId = +fieldIdParam;
        }

        this.getFieldInfo();
        this.loadChemCompareTypes();
    }

    ngAfterViewInit(): void {
        this.initMaps();
        this.mapsChangeSub = this.maps.changes.subscribe(() => this.initMaps());

        this.mapSyncSub = this.chemCompareMapSyncService.mapState$.subscribe(state => {
            // syncAllMapsDirectly in the service handles syncing other maps.
            // This subscription is only needed for external state changes (not from this component).
        });

        this.zoomSub = this.chemCompareMapSyncService.zoomCommand$.subscribe(delta => {
            this.isUpdatingFromSync = true;
            const firstMap = this.maps.first?.googleMap;
            const currentZoom = firstMap?.getZoom();
            if (currentZoom == null) {
                this.isUpdatingFromSync = false;
                return;
            }
            const newZoom = Math.max(1, Math.min(22, currentZoom + delta));
            this.maps.forEach(map => {
                if (map?.googleMap) {
                    map.googleMap.setZoom(newZoom);
                }
            });
            setTimeout(() => this.isUpdatingFromSync = false, 300);
        });

        this.resetSub = this.chemCompareMapSyncService.reset$.subscribe(() => {
            this.isUpdatingFromSync = true;
            const allEntries = [...this.chemCompareLeft.entries, ...this.chemCompareRight.entries];
            const visibleEntries = allEntries.filter(e => e.mapUrl && e.bounds);
            this.maps.forEach((map, index) => {
                const entry = visibleEntries[index];
                if (map?.googleMap && entry) {
                    const bounds = this.getEntryBounds(entry);
                    if (bounds) map.fitBounds(bounds);
                }
            });
            setTimeout(() => this.isUpdatingFromSync = false, 300);
        });

        this.legendSub = this.chemCompareMapSyncService.showLegends$.subscribe(show => {
            this.showLegends = show;
        });
    }

    ngOnDestroy(): void {
        this.maps?.forEach((_, index) => {
            this.chemCompareMapSyncService.unregisterMap(`chem-map-${index}`);
        });
        this.mapSyncSub?.unsubscribe();
        this.mapsChangeSub?.unsubscribe();
        this.zoomSub?.unsubscribe();
        this.resetSub?.unsubscribe();
        this.legendSub?.unsubscribe();
        this.cleanupCtrlScrollZoom();
        this.chemicalCompareService.clearCache();
        this.chemCompareMapSyncService.resetAll();
        this.chemCompareMapSyncService.resetLegends();
    }

    // --- Header actions ---

    closeView(): void {
        this.closed.emit();
        this.router.navigate(['field'], { replaceUrl: true });
    }

    zoomIn(): void {
        this.chemCompareMapSyncService.zoomAll(0.5);
    }

    zoomOut(): void {
        this.chemCompareMapSyncService.zoomAll(-0.5);
    }

    resetMaps(): void {
        this.chemCompareMapSyncService.resetAll();
    }

    toggleLegends(): void {
        this.chemCompareMapSyncService.toggleLegends();
    }

    // --- Data loading ---

    private loadChemCompareTypes(): void {
        const dto: MapsTypesAvailabilityDto = { farmId: null, fieldId: this.fieldId };
        this.analysisManagementService.getMapsTypesAvailability(dto).subscribe(availableTypes => {
            const types = availableTypes
                .filter(t => t.type?.toLowerCase().includes('chemical') && t.alias !== 'CHEMICAL COMPARE')
                .map(t => ({ type: t.type, alias: t.alias }));

            this.chemCompareChemicalTypes = types;
            this.chemicalCompareService.setChemicalTypes(types);

            this.chemCompareLeft = { soilType: 'TOP_SOIL', availableYears: [], allMaps: [], selectedYear: null, entries: [] };
            this.chemCompareRight = { soilType: 'TOP_SOIL', availableYears: [], allMaps: [], selectedYear: null, entries: [] };

            this.loadChemCompareYears(this.chemCompareLeft);
            this.loadChemCompareYears(this.chemCompareRight);
        });
    }

    onSoilTypeChange(side: ChemicalCompareSideModel): void {
        side.selectedYear = null;
        side.availableYears = [];
        side.allMaps = [];
        side.entries = [];
        this.loadChemCompareYears(side);
    }

    onYearChange(side: ChemicalCompareSideModel): void {
        this.buildEntries(side);
    }

    private loadChemCompareYears(side: ChemicalCompareSideModel): void {
        this.nav.toggleLoading(true);
        this.chemicalCompareService.loadYearsForSoilType(this.fieldId, side.soilType).subscribe({
            next: data => {
                side.allMaps = data.allMaps;
                side.availableYears = data.availableYears;
                this.nav.toggleLoading(false);
            },
            error: () => this.nav.toggleLoading(false)
        });
    }

    private buildEntries(side: ChemicalCompareSideModel): void {
        const match = side.allMaps.find(m => dayjs(m.datePerformed).format('YYYY') === side.selectedYear);
        const items = match?.items || [];

        if (!match || items.length === 0) {
            side.entries = [];
            this.buildAlignedEntries();
            return;
        }

        const requests = items
            .filter(item => item.mapUrl)
            .map(item => ({
                key: `${match.analysisId}:${item.propertyTypeId}`,
                analysisId: match.analysisId,
                propertyTypeId: item.propertyTypeId
            }));

        if (requests.length === 0) {
            side.entries = items.map(item => ({
                propertyTypeName: item.propertyTypeName,
                mapUrl: item.mapUrl || null,
                analysisId: match.analysisId,
                propertyTypeId: item.propertyTypeId,
                bounds: null
            }));
            this.buildAlignedEntries();
            return;
        }

        this.nav.toggleLoading(true);
        forkJoin(
            requests.map(r => this.analysisManagementService.getBoundingBox(r.analysisId, r.propertyTypeId).pipe(catchError(() => of(null))))
        ).subscribe(results => {
            const bc: Record<string, any> = {};
            results.forEach((bb, i) => { if (bb) bc[requests[i].key] = bb; });

            side.entries = items.map(item => {
                if (!item.mapUrl) return { propertyTypeName: item.propertyTypeName, mapUrl: null, bounds: null, analysisId: null, propertyTypeId: null };
                const bb = bc[`${match.analysisId}:${item.propertyTypeId}`];
                return {
                    propertyTypeName: item.propertyTypeName,
                    mapUrl: item.mapUrl,
                    analysisId: match.analysisId,
                    propertyTypeId: item.propertyTypeId,
                    bounds: bb ? { lat1: bb.lat1, lng1: bb.lng1, lat2: bb.lat2, lng2: bb.lng2 } : null
                };
            });
            this.buildEntryCaches(side.entries);
            this.loadLegendsForEntries(side.entries);
            this.buildAlignedEntries();
            this.nav.toggleLoading(false);
        });
    }

    /**
     * Builds a unified aligned list from both sides.
     * Each row has a left and right entry matched by propertyTypeName.
     * If one side is missing an element, it gets null (rendered as blank space).
     */
    private buildAlignedEntries(): void {
        const leftEntries = this.chemCompareLeft.entries;
        const rightEntries = this.chemCompareRight.entries;

        // Collect all unique property type names in order (left first, then any extras from right)
        const allNames: string[] = [];
        const seen = new Set<string>();

        for (const entry of leftEntries) {
            if (!seen.has(entry.propertyTypeName)) {
                allNames.push(entry.propertyTypeName);
                seen.add(entry.propertyTypeName);
            }
        }
        for (const entry of rightEntries) {
            if (!seen.has(entry.propertyTypeName)) {
                allNames.push(entry.propertyTypeName);
                seen.add(entry.propertyTypeName);
            }
        }

        // Build lookup maps
        const leftMap = new Map<string, ChemicalCompareEntry>();
        for (const entry of leftEntries) {
            leftMap.set(entry.propertyTypeName, entry);
        }
        const rightMap = new Map<string, ChemicalCompareEntry>();
        for (const entry of rightEntries) {
            rightMap.set(entry.propertyTypeName, entry);
        }

        // Build aligned pairs
        this.alignedEntries = allNames.map(name => ({
            left: leftMap.get(name) || null,
            right: rightMap.get(name) || null
        }));
    }

    // --- Map sync ---

    onMapDragEnd(mapArrayIndex: number): void {
        if (this.isUpdatingFromSync) return;
        this.syncFromMap(mapArrayIndex);
    }

    onMapZoomChanged(mapArrayIndex: number): void {
        if (this.isUpdatingFromSync) return;
        this.syncFromMap(mapArrayIndex);
    }

    private syncFromMap(mapArrayIndex: number): void {
        const mapArray = this.maps.toArray();
        const map = mapArray[mapArrayIndex];
        if (!map?.googleMap) return;
        const center = map.googleMap.getCenter();
        const zoom = map.googleMap.getZoom();
        if (!center || zoom == null) return;

        this.isUpdatingFromSync = true;
        this.chemCompareMapSyncService.updateMapState({ center, zoom, bounds: null, sourceMapId: `chem-map-${mapArrayIndex}` });
        setTimeout(() => this.isUpdatingFromSync = false, 50);
    }

    private initMaps(): void {
        this.registerMaps();
        this.setupCtrlScrollZoom();
        this.fitAllMaps();
    }

    private fitAllMaps(): void {
        const allEntries = [...this.chemCompareLeft.entries, ...this.chemCompareRight.entries];
        const visibleEntries = allEntries.filter(e => e.mapUrl && e.bounds);
        setTimeout(() => {
            this.maps.forEach((map, index) => {
                const entry = visibleEntries[index];
                if (map?.googleMap && entry) {
                    const bounds = this.getEntryBounds(entry);
                    if (bounds) map.fitBounds(bounds);
                }
            });
        }, 100);
    }

    private registerMaps(): void {
        // Clear old registrations to avoid stale references to destroyed maps
        this.chemCompareMapSyncService.clearRegistry();
        this.maps.forEach((map, index) => {
            if (map?.googleMap) {
                this.chemCompareMapSyncService.registerMap(`chem-map-${index}`, map);
            }
        });
    }

    getVisibleIndex(side: string, entryIndex: number): number {
        const entries = side === 'L' ? this.chemCompareLeft.entries : this.chemCompareRight.entries;
        const visibleBefore = side === 'R'
            ? this.chemCompareLeft.entries.filter(e => e.mapUrl && e.bounds).length
            : 0;
        const visibleInSide = entries.slice(0, entryIndex).filter(e => e.mapUrl && e.bounds).length;
        return visibleBefore + visibleInSide;
    }

    // --- Ctrl+Scroll zoom (matches sequence-card behaviour) ---

    private setupCtrlScrollZoom(): void {
        this.cleanupCtrlScrollZoom();

        // Block browser's native Ctrl+scroll page zoom when cursor is over any map
        this.maps.forEach(map => {
            if (!map?.googleMap) return;
            const mapDiv = map.googleMap.getDiv();
            const handler = (e: WheelEvent) => {
                if (e.ctrlKey) {
                    e.preventDefault();
                }
            };
            mapDiv.addEventListener('wheel', handler, { passive: false });
            this.wheelListeners.push({ div: mapDiv, handler });
        });

        // Toggle Google Maps' own scrollwheel zoom based on Ctrl key state
        this.onKeyDown = (e: KeyboardEvent) => {
            if (e.key === 'Control') {
                this.maps.forEach(map => {
                    if (map?.googleMap) {
                        map.googleMap.setOptions({ scrollwheel: true });
                    }
                });
            }
        };

        this.onKeyUp = (e: KeyboardEvent) => {
            if (e.key === 'Control') {
                this.maps.forEach(map => {
                    if (map?.googleMap) {
                        map.googleMap.setOptions({ scrollwheel: false });
                    }
                });
            }
        };

        this.onBlur = () => {
            this.maps.forEach(map => {
                if (map?.googleMap) {
                    map.googleMap.setOptions({ scrollwheel: false });
                }
            });
        };

        window.addEventListener('keydown', this.onKeyDown);
        window.addEventListener('keyup', this.onKeyUp);
        window.addEventListener('blur', this.onBlur);
    }

    private cleanupCtrlScrollZoom(): void {
        this.wheelListeners.forEach(({ div, handler }) => div.removeEventListener('wheel', handler));
        this.wheelListeners = [];
        if (this.onKeyDown) window.removeEventListener('keydown', this.onKeyDown);
        if (this.onKeyUp) window.removeEventListener('keyup', this.onKeyUp);
        if (this.onBlur) window.removeEventListener('blur', this.onBlur);
    }

    // --- Bounds & center cache ---

    getEntryBounds(entry: ChemicalCompareEntry): google.maps.LatLngBounds | null {
        if (!entry.analysisId || !entry.propertyTypeId) return null;
        return this.boundsCache[`${entry.analysisId}:${entry.propertyTypeId}`] || null;
    }

    getEntryCenter(entry: ChemicalCompareEntry): google.maps.LatLngLiteral | null {
        if (!entry.analysisId || !entry.propertyTypeId) return null;
        return this.centerCache[`${entry.analysisId}:${entry.propertyTypeId}`] || null;
    }

    private buildEntryCaches(entries: ChemicalCompareEntry[]): void {
        entries.forEach(entry => {
            if (!entry.bounds || !entry.analysisId || !entry.propertyTypeId) return;
            const key = `${entry.analysisId}:${entry.propertyTypeId}`;
            if (!this.boundsCache[key]) {
                const b = new google.maps.LatLngBounds();
                b.extend(new google.maps.LatLng(entry.bounds.lat1, entry.bounds.lng1));
                b.extend(new google.maps.LatLng(entry.bounds.lat2, entry.bounds.lng2));
                this.boundsCache[key] = b;
                this.centerCache[key] = {
                    lat: (entry.bounds.lat1 + entry.bounds.lat2) / 2,
                    lng: (entry.bounds.lng1 + entry.bounds.lng2) / 2
                };
            }
        });
    }

    // --- Legends ---

    getLegends(entry: ChemicalCompareEntry): FieldLegend[] {
        if (!entry.analysisId || !entry.propertyTypeId) return [];
        return this.legendsMap[`${entry.analysisId}:${entry.propertyTypeId}`] || [];
    }

    private loadLegendsForEntries(entries: ChemicalCompareEntry[]): void {
        const toLoad: { key: string, analysisId: number, propertyTypeId: number }[] = [];
        entries.forEach(entry => {
            if (!entry.analysisId || !entry.propertyTypeId || !entry.mapUrl) return;
            const key = `${entry.analysisId}:${entry.propertyTypeId}`;
            if (this.legendsLoaded.has(key)) return;
            this.legendsLoaded.add(key);
            toLoad.push({ key, analysisId: entry.analysisId, propertyTypeId: entry.propertyTypeId });
        });
        this.loadLegendSequential(toLoad, 0);
    }

    private loadLegendSequential(items: { key: string, analysisId: number, propertyTypeId: number }[], index: number): void {
        if (index >= items.length) return;
        const item = items[index];
        const dto: FieldImageryLegendDto = { analysisId: item.analysisId, propertyTypeId: item.propertyTypeId };
        this.analysisManagementService.getFieldImageryLegend(dto).subscribe({
            next: (result) => {
                if (result && Array.isArray(result)) {
                    this.legendsMap[item.key] = this.processLegendItems(result);
                }
                this.loadLegendSequential(items, index + 1);
            },
            error: () => this.loadLegendSequential(items, index + 1)
        });
    }

    private processLegendItems(items: any[]): FieldLegend[] {
        return items.map(item => {
            let value = '';
            if (item.textValue == null || item.textValue == 'null') {
                value = item.maxValue != 1000000
                    ? item.minValue?.toFixed(2) + ' - ' + item.maxValue?.toFixed(2)
                    : '> ' + item.minValue?.toFixed(2);
            } else {
                value = item.textValue;
            }
            return { value, color: item.color || item.colour, area: item.area || '' } as FieldLegend;
        });
    }

    getFieldInfo(): void {
        const fieldData = sessionStorage.getItem('currentFieldInfo');
        if (fieldData) {
            const fieldObj = JSON.parse(fieldData);
            this.fieldName = fieldObj?.fieldName || '';
            this.fieldSize = fieldObj?.fieldSize ? Number(fieldObj.fieldSize) : null;
        }
    }
}
